import { useMemo } from "react";
import { parseSchedule } from "../../components/SchedulePicker";
import s from "./ScheduleGrid.module.css";

const DAYS = [
  { key: "Mon", label: "Mon" },
  { key: "Tue", label: "Tue" },
  { key: "Wed", label: "Wed" },
  { key: "Thu", label: "Thu" },
  { key: "Fri", label: "Fri" },
];
const DAY_KEYS = DAYS.map(d => d.key);
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const HOUR_PX = 56;
const FIRST_HOUR = HOURS[0];

/** Color palette for class blocks — hashed by a stable seed (subjectId
 *  or sectionId) so the same block lands on the same color across
 *  renders. Muted gradients so multiple blocks coexist without fighting
 *  for attention. */
const PALETTE = [
  { bg: "linear-gradient(135deg,#fef3c7,#fde68a)", fg: "#7c4a03", accent: "#c9a227" },
  { bg: "linear-gradient(135deg,#dbeafe,#bfdbfe)", fg: "#1e3a8a", accent: "#2563eb" },
  { bg: "linear-gradient(135deg,#dcfce7,#bbf7d0)", fg: "#14532d", accent: "#16a34a" },
  { bg: "linear-gradient(135deg,#fce7f3,#fbcfe8)", fg: "#831843", accent: "#db2777" },
  { bg: "linear-gradient(135deg,#ede9fe,#ddd6fe)", fg: "#3b0764", accent: "#7c3aed" },
  { bg: "linear-gradient(135deg,#ffe4e6,#fecdd3)", fg: "#7f1d1d", accent: "#dc2626" },
  { bg: "linear-gradient(135deg,#e0f2fe,#bae6fd)", fg: "#0c4a6e", accent: "#0284c7" },
  { bg: "linear-gradient(135deg,#fef3e2,#fed7aa)", fg: "#7c2d12", accent: "#ea580c" },
];
function paletteFor(seed) {
  const id = Number(seed) || 0;
  return PALETTE[Math.abs(id) % PALETTE.length];
}

/** Convert an "HH:MM[:SS]" string to a fractional hour (8.5 = 8:30). */
function toHourFraction(t) {
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const h  = Number(m[1]);
  const mn = Number(m[2]);
  return h + mn / 60;
}

/**
 * Read-only Mon–Fri schedule grid. Reuses parseSchedule from
 * SchedulePicker so the same free-text formats that the rest of the
 * app accepts work here too. Click a block to open the parent's
 * detail handler.
 *
 * Props:
 *   offerings    Array<ClassOffering>  — pre-filtered to whatever
 *                                        scope the parent wants (e.g.
 *                                        all offerings for a section
 *                                        in the active term).
 *   subjects     Array<Subject>
 *   sections     Array<Section>
 *   employees    Array<Employee>
 *   rooms        Array<Room>
 *   gradeLevels  Array<GradeLevel>
 *   colorBy      "subject" (default) | "section"  — what hashes the
 *                                                   block color.
 *   primaryFor   "section" (default) | "room"     — controls which
 *                                                   field leads in the
 *                                                   block's title row.
 *   onBlockClick fn(offering)
 */
export default function ScheduleGrid({
  offerings = [],
  subjects  = [],
  sections  = [],
  employees = [],
  rooms     = [],
  gradeLevels = [],
  colorBy   = "subject",
  primaryFor = "section",
  onBlockClick,
}) {
  const subjectsById = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);
  const sectionsById = useMemo(() => Object.fromEntries(sections.map(x => [x.id, x])), [sections]);
  const employeesById= useMemo(() => Object.fromEntries(employees.map(x => [x.id, x])), [employees]);
  const roomsById    = useMemo(() => Object.fromEntries(rooms.map(x => [x.id, x])),    [rooms]);
  const gradeById    = useMemo(() => Object.fromEntries(gradeLevels.map(x => [x.id, x])), [gradeLevels]);

  /** Parse each offering into placeable blocks (one per day in the
   *  schedule string). Anything we can't parse goes into the
   *  "unscheduled" list shown below the grid. */
  const { placed, unscheduled } = useMemo(() => {
    const placed = [];           // { offering, day, top, height, palette, ... }
    const unscheduled = [];      // offerings we couldn't place
    for (const o of offerings) {
      const parsed = parseSchedule(o.schedule || "");
      const sH = toHourFraction(parsed.start);
      const eH = toHourFraction(parsed.end);
      if (parsed.days.length === 0 || sH == null || eH == null || eH <= sH) {
        unscheduled.push(o);
        continue;
      }
      const top    = (sH - FIRST_HOUR) * HOUR_PX;
      const height = Math.max(20, (eH - sH) * HOUR_PX - 4);  // -4 for vertical breathing room
      const seed = colorBy === "section" ? o.sectionId : o.subjectId;
      const palette = paletteFor(seed);
      for (const day of parsed.days) {
        if (!DAY_KEYS.includes(day)) continue;  // skip Sat/Sun for the 5-day grid
        placed.push({ offering: o, day, top, height, palette });
      }
    }
    return { placed, unscheduled };
  }, [offerings, colorBy]);

  if (offerings.length === 0) {
    return (
      <div className={s.empty}>
        <div className={s.emptyTitle}>No classes scheduled</div>
        <div className={s.emptySub}>
          The week is clear for the current term.
        </div>
      </div>
    );
  }

  // CSS variable so the day-column hour-stripe pattern matches HOUR_PX.
  const styleVar = { "--hour-px": `${HOUR_PX}px` };

  return (
    <div className={s.wrap} style={styleVar}>
      <div className={s.grid}>
        {/* Header row: time spacer + day labels */}
        <div className={s.headCellTime}>Time</div>
        {DAYS.map(d => (
          <div key={d.key} className={s.headCell}>{d.label}</div>
        ))}

        {/* Time rail (column 1) — one cell per hour, sized to HOUR_PX */}
        <div style={{ gridColumn: "1", display: "grid", gridAutoRows: `${HOUR_PX}px` }}>
          {HOURS.map(h => (
            <div key={h} className={s.timeCell}>
              {h % 12 === 0 ? 12 : h % 12}{h < 12 ? " AM" : " PM"}
            </div>
          ))}
        </div>

        {/* Five day columns — each fills the full HOURS height and
            absolute-positions its blocks within. */}
        {DAYS.map(d => {
          const blocksHere = placed.filter(b => b.day === d.key);
          return (
            <div
              key={d.key}
              className={s.dayCol}
              style={{ height: HOURS.length * HOUR_PX, position: "relative" }}>
              {blocksHere.map((b, i) => {
                const subj = subjectsById[b.offering.subjectId];
                const sec  = sectionsById[b.offering.sectionId];
                const grade= sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                const tea  = b.offering.teacherStaffId ? employeesById[b.offering.teacherStaffId] : null;
                const room = b.offering.roomId ? roomsById[b.offering.roomId] : null;

                // Title leads with whatever the consumer asked for.
                let title, meta;
                if (primaryFor === "room") {
                  // Room-detail context: lead with the section that's
                  // meeting here, then subject + teacher in meta.
                  title = sec ? (grade ? `${grade.name} · ${sec.name}` : sec.name) : "—";
                  meta  = `${subj?.code || ""}${subj?.code && tea?.name ? " · " : ""}${tea?.name || ""}`;
                } else {
                  // Section-detail (default): subject leads, teacher +
                  // room in meta.
                  title = subj?.title || subj?.code || "—";
                  const roomLabel = room?.name || b.offering.room || "";
                  meta = `${tea?.name || "—"}${roomLabel ? " · " : ""}${roomLabel}`;
                }
                return (
                  <button
                    key={`${b.offering.id}-${i}`}
                    type="button"
                    className={s.block}
                    style={{
                      top: b.top,
                      height: b.height,
                      background: b.palette.bg,
                      color: b.palette.fg,
                      borderLeftColor: b.palette.accent,
                    }}
                    onClick={() => onBlockClick?.(b.offering)}
                    title={`${title} — ${meta}`}>
                    <span className={s.blockTitle}>{title}</span>
                    <span className={s.blockMeta}>{meta}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <div className={s.unscheduledWrap}>
          <div className={s.unscheduledTitle}>Unscheduled</div>
          <div>
            {unscheduled.map(o => {
              const subj = subjectsById[o.subjectId];
              const sec  = sectionsById[o.sectionId];
              const label = primaryFor === "room"
                ? (sec?.name || subj?.title || "—")
                : (subj?.title || subj?.code || "—");
              return (
                <button
                  key={o.id}
                  type="button"
                  className={s.unscheduledItem}
                  onClick={() => onBlockClick?.(o)}
                  title={o.schedule || "No schedule set"}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
